/**
 * <event-console> — the fully emancipated event detail page: a full-bleed
 * identity header, a horizontal nav rail beneath it carrying the event's own
 * destinations (Dashboard, Orders, Analytics, Edit, Check-in), and the content
 * pane below — each destination a routed component in this component's own
 * nested `event-console` area.
 *
 * The identity header is `<console-headline>` (packages/views/src/events/
 * console/console-headline.view.ts) — event identity, schedule/venue, the
 * money cell, and the live "who is here right now" headcount, all in one
 * band. It carries no sell-through mark of its own: sold-of-quota is a
 * per-type answer the programme block beneath states on the row an operator
 * can act on (console-headline.view.ts's own doc comment).
 *
 * Destinations are routed, not a `@state()` template branch: schmancy's area
 * carries the active destination in its own URL segment and in `area.on()`,
 * so navigating between destinations (or reloading on one) survives exactly
 * like every other area in this app — a local field cannot do either
 * (skills/schmancy/area.md; web/src/admin/settings/settings.view.ts
 * is this app's own precedent for a nested section rail built the same way).
 *
 * Routed by web at admin-shell.view.ts's "admin" area — web's own
 * event-console.view.ts wrapper mounts `<event-console-sync>` beside this
 * component; this component itself stays zero-I/O, taking `eventId` as a
 * plain property input (its source differs by host app — a route in web, a
 * picker in the designer studio — which is why it isn't a shared state
 * signal).
 */
import { area, lazy } from "@mhmo91/schmancy";
import { fill, overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { computed } from "@mhmo91/schmancy/state";
import { html, type TemplateResult } from "lit";
import { property, state as stateDecorator } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { combineLatest, takeUntil, tap } from "rxjs";
import {
	canViewSaleReports,
	saleCatalogStatus,
	saleEvents,
	saleTicketTypes,
	selectedSaleEventId,
} from "../../admin/sale/sale.state.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { organizationId } from "../../workspace/organization.state.ts";
import "./analytics.view.ts";
import "./checkin.view.ts";
import "./console-headline.view.ts";
import "./dashboard.view.ts";
import "./edit.view.ts";
import "./orders.view.ts";
import "../../checkin/index.ts";
import {
	canManageVouchers,
	vouchersEventId$,
} from "../../vouchers/vouchers.state.ts";
import { canManageEvents, eventsState, eventsStatus } from "../events.state.ts";
import {
	ticketTypesEventId$,
	ticketTypesState,
} from "../ticket-types/ticket-types.state.ts";
import { canManageAds } from "./campaign/campaign.state.ts";
import {
	CONSOLE_SEGMENT,
	consoleAddress,
	consoleDestination,
	consoleEvent,
	consoleEventId,
} from "./console.state.ts";
import { canCheckInTickets } from "./order-ops.state.ts";

type Destination =
	| "event-console-dashboard"
	| "event-console-orders"
	| "event-console-analytics"
	| "event-console-campaign"
	| "event-console-edit"
	| "event-console-checkin"
	| "voucher-list";

// Each item's `value` is the tag its route mounts, which is what the nested
// `event-console` area reports back; its route's `when` is that tag's address
// segment (CONSOLE_SEGMENT).
// Each `component` carries its own return annotation so every entry shares one
// loader signature — without it the entries' module types stay distinct and the
// area's `.default` below resolves against whichever entry happens to be first.
const RAIL_ITEMS = [
	{
		value: "event-console-dashboard",
		labelKey: "admin.events.console.navDashboard",
		icon: "monitoring",
		component: (): Promise<{ default: CustomElementConstructor }> =>
			import("./dashboard.view.ts"),
	},
	{
		value: "event-console-orders",
		labelKey: "admin.events.console.navOrders",
		icon: "receipt_long",
		component: (): Promise<{ default: CustomElementConstructor }> =>
			import("./orders.view.ts"),
	},
	{
		value: "event-console-analytics",
		labelKey: "admin.events.console.navAnalytics",
		icon: "query_stats",
		component: (): Promise<{ default: CustomElementConstructor }> =>
			import("./analytics.view.ts"),
	},
	{
		value: "event-console-edit",
		labelKey: "admin.events.console.navEdit",
		icon: "edit",
		component: (): Promise<{ default: CustomElementConstructor }> =>
			import("./edit.view.ts"),
	},
	{
		// A code belongs to exactly one event
		// (`events/{eventId}/vouchers/{code}`), so this is the only rail it can
		// hang from: the destination the operator reaches it through is what
		// answers "which event", and it is why the form below carries no event
		// picker of its own. Its tag is the component's own name rather than an
		// `event-console-*` one — the view lives in `vouchers/` and is mountable
		// wherever a console event id resolves.
		value: "voucher-list",
		labelKey: "admin.vouchers.nav",
		// A price tag, not the ticket glyph the Events rail already owns: a code
		// is what comes off a price, and this rail sits one level inside an
		// event where every row would otherwise be a ticket.
		icon: "sell",
		component: (): Promise<{ default: CustomElementConstructor }> =>
			import("../../vouchers/voucher-list.view.ts"),
	},
	{
		// A campaign promotes exactly one event and is stored under it, so this
		// is the only rail it can hang from: the destination the operator
		// reaches it through is what answers "which event". Not eagerly
		// imported the way its siblings are — the module registers tags a
		// design-lab proof of the same surface still registers, and a lazy
		// route is what keeps the two from meeting in one page.
		value: "event-console-campaign",
		labelKey: "admin.campaign.nav",
		icon: "campaign",
		component: (): Promise<{ default: CustomElementConstructor }> =>
			import("./campaign/campaign.view.ts"),
	},
	{
		// Reuses the door's own MessageKey ("Check-in"/"Einlass") rather than
		// coining a new admin.nav.* string — same feature, same word (mirrors
		// the retired top-level nav-rail item's own reasoning).
		value: "event-console-checkin",
		labelKey: "scanner.session.title",
		icon: "qr_code_scanner",
		component: (): Promise<{ default: CustomElementConstructor }> =>
			import("./checkin.view.ts"),
	},
] as const satisfies ReadonlyArray<{
	readonly value: Destination;
	readonly labelKey: string;
	readonly icon: string;
	readonly component: () => Promise<{ default: CustomElementConstructor }>;
}>;

const visibleRailItems = computed(() =>
	RAIL_ITEMS.filter((item) => {
		switch (item.value) {
			case "event-console-dashboard":
			case "event-console-orders":
			case "event-console-analytics":
				return canViewSaleReports.get();
			case "event-console-edit":
				return canManageEvents.get();
			case "voucher-list":
				return canManageVouchers.get();
			case "event-console-campaign":
				return canManageAds.get();
			case "event-console-checkin":
				return canCheckInTickets.get();
			default:
				return false;
		}
	}),
);

@schmancyElement("event-console")
export class EventConsoleView extends LocalizeMixin(SchmancyElement) {
	@property({ type: String }) eventId = "";
	/** Where the host addresses this console; each destination's segment
	 *  follows it. Empty where the host names no address. */
	@property({ type: String }) address = "";
	/** Mirrors the nested `event-console` area so the rail's own highlight
	 *  survives a reload/deeplink into a destination, and follows browser
	 *  back/forward — not just this component's own click handler. */
	@stateDecorator() private activeDestination: Destination =
		"event-console-dashboard";

	override connectedCallback(): void {
		super.connectedCallback();
		// An entry opens on the Dashboard: the router keeps the destination
		// area's last push to replay when it next mounts, which would reopen
		// another event's console on the tab it was left on.
		area.consumePending("event-console");
		consoleEventId.set(this.eventId || null);
		consoleAddress.set(this.address || null);
		ticketTypesEventId$.next(this.eventId || null);
		// Opened for the whole console, not just the Codes destination: the
		// ticket-type form's audience answer states which of this event's codes
		// reveal a code-gated type, and that hint is unbuildable without the
		// same collection this listener already carries.
		vouchersEventId$.next(this.eventId || null);

		area
			.on("event-console")
			.pipe(
				tap((route) => {
					const item = RAIL_ITEMS.find((i) => i.value === route.component);
					if (item) this.activeDestination = item.value;
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe();

		// Scopes the shared sale catalog (admin/sale/sale.state.ts) to exactly
		// this event, purely from already-loaded state — no Firestore read of
		// its own. <sale-sync> (web) never mounts inside the console, so
		// without this fold guest-issue.view.ts would see last session's
		// leftover catalog (or nothing) instead of this event's own types.
		combineLatest([
			consoleEventId.$,
			eventsState.$,
			ticketTypesState.$,
			organizationId.$,
		])
			.pipe(
				tap(([id, events, types, orgId]) => {
					if (!id) {
						saleEvents.replace({ orgId, events: [] });
						selectedSaleEventId.set(null);
						saleTicketTypes.replace([]);
						return;
					}
					const event = events.find((e) => e.id === id);
					saleCatalogStatus.set("ready");
					saleEvents.replace({
						orgId,
						events: event
							? [
									{
										id: event.id,
										name: event.name,
										status: event.status,
										startMs: event.schedule.start.toDate().getTime(),
										schedule: event.schedule,
										locationId: event.locationId,
									},
								]
							: [],
					});
					selectedSaleEventId.set(id);
					saleTicketTypes.replace(types.filter((t) => t.kind === "ticket"));
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe();
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback();
		consoleEventId.set(null);
		consoleAddress.set(null);
		ticketTypesEventId$.next(null);
		vouchersEventId$.next(null);
		// The connect-time fold above (admin/sale/sale.state.ts's own doc
		// comment names sale.sync.ts as its "sole writer") is a foreign write
		// this component makes only for its own lifetime — released here so
		// leaving the console doesn't strand admin/sale's shared singletons on
		// this one event's snapshot for whoever reads them next (guest-issue-view's
		// own <sale-sync>, mounted separately, re-hydrates on its own connect
		// regardless — this closes the gap for any reader in between).
		saleEvents.replace({ orgId: null, events: [] });
		selectedSaleEventId.set(null);
		saleTicketTypes.replace([]);
		saleCatalogStatus.set("loading");
	}

	override willUpdate(changed: Map<PropertyKey, unknown>): void {
		if (changed.has("eventId")) {
			consoleEventId.set(this.eventId || null);
			ticketTypesEventId$.next(this.eventId || null);
			vouchersEventId$.next(this.eventId || null);
		}
		if (changed.has("address")) consoleAddress.set(this.address || null);
	}

	private back(): void {
		this.dispatchEvent(
			new CustomEvent("back", { bubbles: true, composed: true }),
		);
	}

	override render(): TemplateResult {
		const event = consoleEvent.get();
		if (!event) {
			// Absence isn't the same fact on every status: `eventsState` hasn't
			// hydrated yet on a cold reload (status "loading"), so the console
			// must show a skeleton, not declare an event missing it hasn't even
			// heard back about yet. Only once the read has genuinely settled
			// (status "ready", still no matching id — a bad/stale eventId) or
			// truly failed ("error"/"denied") is "not found" an honest claim.
			if (eventsStatus.value === "loading") {
				return html`
					<div role="status" aria-busy="true" class="flex flex-col gap-3 p-4 md:p-6">
						<schmancy-skeleton shape="text" class="w-1/2"></schmancy-skeleton>
						<schmancy-skeleton class="h-32 w-full rounded-2xl"></schmancy-skeleton>
					</div>
				`;
			}
			return html`
				<div class="flex flex-col items-start gap-3 px-6 py-8">
					<schmancy-icon class="text-3xl text-surface-onVariant">event_busy</schmancy-icon>
					<schmancy-typography type="title" token="sm">${this.t("admin.events.console.eventNotFoundTitle")}</schmancy-typography>
				</div>
			`;
		}

		// Check-in takes the whole viewport, the way it takes the whole phone:
		// a scanning shift is a session, not a panel on a dashboard. The rail,
		// the event header and the tab chrome all step aside, and the verdict
		// splash covers the screen rather than being contained to a box.
		//
		// The way out goes into scan-view's own `leading` slot, at the head of
		// its top row: `← <event name>`. No corner of that surface is free to
		// absolutely position over — the door console spans the bottom edge and
		// grows upward as the operator opens it — so the slot is the allocation
		// scan-view makes for exactly this, and it holds in every state of the
		// log rather than depending on which corner the scanner's chrome leaves
		// empty. Positioning, and the iOS safe-area insets that surface applies
		// to every control in that row, come with the slot. The outline does
		// not: this button sits over a live camera frame rather than a rail
		// surface, which the rail's own back affordance never does.
		if (this.activeDestination === "event-console-checkin") {
			return html`
				<div class="fixed inset-0 z-50 bg-surface-default">
					<scan-view class="block h-full w-full">
						<schmancy-icon-button
							slot="leading"
							icon="arrow_back"
							variant="outlined"
							title=${this.t("admin.events.console.back")}
							aria-label=${this.t("admin.events.console.back")}
							@click=${() => {
								// The nested "event-console" area is unmounted here (this
								// branch renders instead of it), so area.push alone is inert —
								// its only listener is the area.on() subscription that fires on
								// MOUNT, and nothing remounts the area without this field write.
								const target = visibleRailItems
									.get()
									.filter((item) => item.value !== "event-console-checkin")[0];
								if (!target) {
									this.back();
									return;
								}
								// Overwrites the stale "event-console-checkin" entry still sitting
								// in pendingByArea (area.pop() never clears it) so the remounting
								// area's own consumePending() lands here, not back on checkin.
								area.push({
									...consoleDestination(target.value),
									historyStrategy: "replace",
								});
								this.activeDestination = target.value;
							}}
						></schmancy-icon-button>
					</scan-view>
				</div>
			`;
		}

		return html`
			<div
				${fill()}
				class="grid grid-cols-[auto_1fr] min-h-0 min-w-0"
			>
				<!-- The console's own column, in the slot the admin rail yielded: an
				     operator inside one event still navigates a rail, not a strip. -->
				<schmancy-navigation-rail
					alignment="top"
					label-visibility="all"
					label-lines="2"
					.activeValue=${this.activeDestination}
					@navigate=${(e: CustomEvent<string>) => {
						// On the rail, never on an item — an absorbed rail renders its rows
						// through a hidden slot, so an item handler never fires, and the
						// drill sheet dispatches here too.
						const item = RAIL_ITEMS.find((i) => i.value === e.detail);
						if (!item) return;
						area.push({
							...consoleDestination(item.value),
							historyStrategy: "replace",
						});
					}}
				>
					<!-- The way out sits above the destinations, in the rail's own
					     header slot: leaving the event is not one of the places inside
					     it, and the header is the one spot in this column that is not a
					     destination. -->
					<schmancy-icon-button
						slot="header"
						icon="arrow_back"
						aria-label=${this.t("admin.events.console.back")}
						@click=${() => this.back()}
					></schmancy-icon-button>
					<!-- Below md the column folds into the navigation sheet, which
					     carries the rail's footer and not its header, so the way out
					     stands there too; the column's own header holds it above md. -->
					<schmancy-button
						slot="footer"
						variant="text"
						class="md:hidden"
						@click=${() => this.back()}
					>
						<schmancy-icon slot="prefix" aria-hidden="true">arrow_back</schmancy-icon>
						${this.t("admin.events.console.back")}
					</schmancy-button>
					${repeat(
						visibleRailItems.get(),
						(item) => item.value,
						(item) => html`
							<schmancy-navigation-rail-item
								value=${item.value}
								label=${this.t(item.labelKey)}
								icon=${item.icon}
							></schmancy-navigation-rail-item>
						`,
					)}
				</schmancy-navigation-rail>

				<div class="grid grid-rows-[auto_1fr] min-h-0 min-w-0">
				<console-headline></console-headline>

				<schmancy-area
					${overflowWithin()}
					name="event-console"
					.default=${lazy(() => (visibleRailItems.get()[0] ?? RAIL_ITEMS[0]).component())}
				>
					${repeat(
						RAIL_ITEMS,
						(item) => item.value,
						(item) =>
							html`<schmancy-route when=${CONSOLE_SEGMENT[item.value]} .component=${lazy(item.component)}></schmancy-route>`,
					)}
					</schmancy-area>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"event-console": EventConsoleView;
	}
}
